import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, XCircle, AlertTriangle, TrendingUp, Filter, Droplets } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import UnifiedContactModal from '../components/UnifiedContactModal';

const SemiAutoCleaningPage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Semi-Automatic Cleaning (BIF & MVC) | Inline Filters | CTC Waterworks"
        description="Semi-automatic cleaning with BIF (Brush-in-Filter) and MVC (Manual Vacuum Cycle) for BIG, BIG-S, and GNG inline filters. Reduce downtime without full teardown."
        canonicalPath="/filtration/inline/cleaning/semi-auto"
      />
      <Navbar />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration/inline" className="text-gray-600 hover:text-ctc-blue">Inline Filters</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration/inline/cleaning" className="text-gray-600 hover:text-ctc-blue">Cleaning Options</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">Semi-Automatic</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Semi-Automatic Cleaning
            </h1>
            <p className="text-2xl text-gray-600 mb-2 font-light">BIF & MVC</p>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Operator-initiated cleaning cycles that reduce downtime without full filter disassembly.
            </p>

            <div className="bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg">
              <p className="text-gray-700 leading-relaxed">
                Semi-automatic tools—BIF (Brush-in-Filter) and MVC (Manual Vacuum Cycle)—allow operators to clean filter screens without removing the head or screen element. Cleaning cycles are manually initiated and typically complete in 5-15 minutes with minimal downtime.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BIF & MVC Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Two Semi-Automatic Technologies</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* BIF Card */}
            <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-lg p-8">
              <Filter className="text-green-600 mb-4" size={48} />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">BIF (Brush-in-Filter)</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                A manual brush mechanism mounted to the filter head that allows operators to rotate a brush against the screen's interior surface using a hand crank, dislodging accumulated debris.
              </p>

              <h4 className="font-bold text-gray-900 mb-3">Best For:</h4>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-2 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700 text-sm">Fine mesh screens with light organic loading</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-2 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700 text-sm">Drip irrigation with frequent but short cleaning cycles</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-2 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700 text-sm">Applications with accessible filter locations</span>
                </li>
              </ul>

              <Link
                to="/filtration/inline/big/brush-cleaning"
                className="text-green-600 font-semibold flex items-center hover:text-green-700"
              >
                Learn More About Brush Technology
                <ArrowRight className="ml-2" size={16} />
              </Link>
            </div>

            {/* MVC Card */}
            <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-lg p-8">
              <Droplets className="text-ctc-blue mb-4" size={48} />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">MVC (Manual Vacuum Cycle)</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                A vacuum scanner mechanism that operators manually actuate to create suction against the screen's interior, pulling debris off and flushing it through a purge valve.
              </p>

              <h4 className="font-bold text-gray-900 mb-3">Best For:</h4>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <CheckCircle className="text-ctc-blue mr-2 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700 text-sm">Coarse perforations with heavy debris (leaves, algae)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-ctc-blue mr-2 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700 text-sm">Surface water sources with variable solids loading</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-ctc-blue mr-2 mt-1 flex-shrink-0" size={18} />
                  <span className="text-gray-700 text-sm">Systems requiring quick, high-flow purge cycles</span>
                </li>
              </ul>

              <Link
                to="/filtration/inline/big/vacuum-cleaning"
                className="text-ctc-blue font-semibold flex items-center hover:text-blue-700"
              >
                Learn More About Vacuum Technology
                <ArrowRight className="ml-2" size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pros & Cons */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Semi-Automatic: Advantages & Limitations</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Pros */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-2xl font-bold text-green-600 mb-6 flex items-center">
                <CheckCircle className="mr-3" size={28} />
                Advantages
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Drastically reduced downtime vs. manual (5-15 min vs. 30-60 min)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">No full disassembly required—head stays in place</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Moderate cost—automation hardware without controls</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Upgradeable to fully automatic (RAB/AVC) later</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-green-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Simple operation with hand crank or manual valve</span>
                </li>
              </ul>
            </div>

            {/* Cons */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-2xl font-bold text-red-600 mb-6 flex items-center">
                <XCircle className="mr-3" size={28} />
                Limitations
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <XCircle className="text-red-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Still requires operator presence to initiate cleaning</span>
                </li>
                <li className="flex items-start">
                  <XCircle className="text-red-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Not ideal for 24/7 operations or remote sites</span>
                </li>
                <li className="flex items-start">
                  <XCircle className="text-red-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Some brief flow interruption or pressure drop during cleaning</span>
                </li>
                <li className="flex items-start">
                  <XCircle className="text-red-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Higher upfront cost than manual-only approach</span>
                </li>
                <li className="flex items-start">
                  <XCircle className="text-red-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Cleaning frequency still relies on manual observation</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Upgrade Path Banner */}
      <section className="py-12 bg-gradient-to-r from-green-600 to-green-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <TrendingUp className="text-white mx-auto mb-4" size={48} />
          <h2 className="text-3xl font-bold text-white mb-4">Ready for Near-Zero Downtime?</h2>
          <p className="text-xl text-green-100 mb-8">
            Upgrade to RAB & AVC fully automatic cleaning with ΔP/time-based triggers for uninterrupted operation and minimal labor.
          </p>
          <Link
            to="/filtration/inline/cleaning/automatic"
            className="bg-white text-green-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center shadow-lg"
          >
            <span>Explore Fully Automatic Options</span>
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>

      {/* Compatible Series */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Semi-Automatic Compatible Series</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/filtration/inline/big"
              className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow border-2 border-gray-200 hover:border-ctc-blue"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-3">BIG Series</h3>
              <p className="text-gray-700 text-sm mb-4">
                Supports BIF and MVC semi-automatic cleaning assemblies.
              </p>
              <div className="text-ctc-blue font-semibold flex items-center">
                View BIG Series
                <ArrowRight className="ml-2" size={16} />
              </div>
            </Link>

            <Link
              to="/filtration/inline/big-s"
              className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow border-2 border-gray-200 hover:border-gray-600"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-3">BIG-S Series</h3>
              <p className="text-gray-700 text-sm mb-4">
                Supports BIF and MVC semi-automatic cleaning assemblies.
              </p>
              <div className="text-gray-600 font-semibold flex items-center">
                View BIG-S Series
                <ArrowRight className="ml-2" size={16} />
              </div>
            </Link>

            <Link
              to="/filtration/inline/gng"
              className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow border-2 border-gray-200 hover:border-green-600"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-3">GNG Series</h3>
              <p className="text-gray-700 text-sm mb-4">
                Supports BIF and MVC semi-automatic cleaning assemblies.
              </p>
              <div className="text-green-600 font-semibold flex items-center">
                View GNG Series
                <ArrowRight className="ml-2" size={16} />
              </div>
            </Link>
          </div>

          {/* TIG Notice */}
          <div className="mt-8 bg-orange-50 border-l-4 border-orange-600 p-6 rounded-r-lg">
            <div className="flex items-start">
              <AlertTriangle className="text-orange-600 mr-3 mt-1 flex-shrink-0" size={24} />
              <div>
                <h4 className="font-bold text-gray-900 mb-2">TIG Series Note</h4>
                <p className="text-gray-700 leading-relaxed">
                  TIG filters do not support Brush/Vacuum/RAB cleaning options. Follow standard TIG maintenance procedures only.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Reduce Cleaning Downtime?</h2>
          <p className="text-xl text-gray-600 mb-8">
            Our filtration specialists can help you select the right semi-automatic cleaning solution for your application.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setIsContactModalOpen(true)}
              className="bg-ctc-blue text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <span>Talk to a Filtration Specialist</span>
              <ArrowRight className="ml-2" size={20} />
            </button>

            <Link
              to="/filtration/inline/cleaning"
              className="border-2 border-ctc-blue text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-ctc-blue hover:text-white transition-colors inline-flex items-center justify-center"
            >
              Compare All Options
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      <UnifiedContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        productType="filtration"
        formSource="product_quote"
      />
    </div>
  );
};

export default SemiAutoCleaningPage;
